import React, { useCallback, useEffect, useState } from 'react';
import { debounce, throttle } from 'lodash';
import { useTranslate } from 'react-admin';

import { Grid } from '@material-ui/core';

import { SelectInput, TextInput } from '~/components';

import { createConsent, CreateOrUpdateConsentRequest, updateConsent } from '~/api/settlement';
import { Amount, Account } from '~/types';
import { HeadTransaction } from '~/types/HeadTransaction';
import { ConsentHolder, RunTransferActionFormProps } from './RunTransferActionTypes';

import { l } from './RunTransferAction';
import AmountInput from '~/components/AmountInput';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';

type SettlementSession = {
  isSending: boolean;
  nextRequest?: CreateOrUpdateConsentRequest;
  lastConsent?: HeadTransaction;
};

const SETTLEMENT_SESSION: SettlementSession = {
  isSending: false,
};

const NO_CONSENT: ConsentHolder = {};

const SettlementForm = ({ resource, profileId, onConsent }: RunTransferActionFormProps) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const isTenantResource = resource === 'tenant/accounts';
  const [direction, setDirection] = useState<'outbound' | 'inbound'>();
  const previewDirection = direction ?? 'outbound';

  const debitAccountSource =
    isTenantResource || previewDirection === 'outbound' ? 'profile' : 'tenant';
  const creditAccountSource =
    isTenantResource || previewDirection === 'inbound' ? 'profile' : 'tenant';
  const [debitAccountId, setDebitAccountId] = useState<string | undefined>();
  const [creditAccountId, setCreditAccountId] = useState<string | undefined>();
  const [debitAccount, setDebitAccount] = useState<Account | undefined>();
  const [creditAccount, setCreditAccount] = useState<Account | undefined>();
  const [comment, setComment] = useState<string | undefined>();
  const [reference, setReference] = useState<string | undefined>();
  const [amount, setAmount] = useState<Amount | null>();

  const debitResource = debitAccountSource === 'profile' ? resource : 'tenant/accounts';
  const debitProfileId = debitAccountSource === 'profile' ? profileId : undefined;
  const creditResource = creditAccountSource === 'profile' ? resource : 'tenant/accounts';
  const creditProfileId = creditAccountSource === 'profile' ? profileId : undefined;

  const settlingLabel = isTenantResource ? t('settlingAccountTenant') : t('settlingAccount');
  const debitIsSettlement = isTenantResource
    ? previewDirection === 'inbound'
    : debitAccountSource === 'tenant';
  const creditIsSettlement = isTenantResource
    ? previewDirection === 'outbound'
    : creditAccountSource === 'tenant';
  const debitLabel = debitIsSettlement ? t('settlementAccount') : settlingLabel;
  const creditLabel = creditIsSettlement ? t('settlementAccount') : settlingLabel;

  const debouncedSetComment = useCallback(debounce(setComment, 500), []);
  const debouncedSetReference = useCallback(debounce(setReference, 500), []);

  useEffect(() => {
    SETTLEMENT_SESSION.isSending = false;
    SETTLEMENT_SESSION.nextRequest = undefined;
    SETTLEMENT_SESSION.lastConsent = undefined;
  }, []);

  const throttledMakeConsent = useCallback(
    throttle(
      (
        debitAccountId: string,
        creditAccountId: string,
        amount: Amount,
        comment?: string,
        reference?: string
      ): any => {
        const request = {
          senderAccountId: debitAccountId,
          recipientAccountId: creditAccountId,
          amount: amount,
          comment: comment,
          referenceId: reference,
        };

        const sendNextRequest = (request: CreateOrUpdateConsentRequest) => {
          if (SETTLEMENT_SESSION.isSending) {
            SETTLEMENT_SESSION.nextRequest = request;
          } else {
            SETTLEMENT_SESSION.isSending = true;
            SETTLEMENT_SESSION.nextRequest = undefined;

            if (SETTLEMENT_SESSION.lastConsent) {
              updateConsent(SETTLEMENT_SESSION.lastConsent.id, request)
                .then((response) => {
                  SETTLEMENT_SESSION.isSending = false;
                  SETTLEMENT_SESSION.lastConsent = response.data;

                  const nextRequest = SETTLEMENT_SESSION.nextRequest;
                  if (nextRequest) {
                    SETTLEMENT_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ consent: response.data });
                })
                .catch((e) => {
                  const errorCode = e?.body?.code;
                  SETTLEMENT_SESSION.isSending = false;

                  const nextRequest = SETTLEMENT_SESSION.nextRequest;
                  if (nextRequest) {
                    SETTLEMENT_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ error: errorCode });
                });
            } else {
              createConsent(request)
                .then((response) => {
                  SETTLEMENT_SESSION.isSending = false;
                  SETTLEMENT_SESSION.lastConsent = response.data;

                  const nextRequest = SETTLEMENT_SESSION.nextRequest;
                  if (nextRequest) {
                    SETTLEMENT_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ consent: response.data });
                })
                .catch((e) => {
                  const errorCode = e?.body.code;
                  SETTLEMENT_SESSION.isSending = false;

                  const nextRequest = SETTLEMENT_SESSION.nextRequest;
                  if (nextRequest) {
                    SETTLEMENT_SESSION.nextRequest = undefined;
                    sendNextRequest(nextRequest);
                  }

                  onConsent({ error: errorCode });
                });
            }
          }
        };

        sendNextRequest(request);
      },
      1000
    ),
    []
  );

  useEffect(() => {
    if (debitAccountId && creditAccountId && amount && comment) {
      onConsent(NO_CONSENT);
      throttledMakeConsent(debitAccountId, creditAccountId, amount, comment, reference);
    } else {
      onConsent(undefined);
    }
  }, [
    onConsent,
    throttledMakeConsent,
    debitAccountId,
    creditAccountId,
    amount,
    comment,
    reference,
  ]);

  const resetAccounts = () => {
    setDebitAccount(undefined);
    setDebitAccountId(undefined);
    setCreditAccount(undefined);
    setCreditAccountId(undefined);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SelectInput<'outbound' | 'inbound' | undefined>
          options={[
            {
              value: 'outbound',
              text: isTenantResource ? t('directionOutboundTenant') : t('directionOutbound'),
            },
            {
              value: 'inbound',
              text: isTenantResource ? t('directionInboundTenant') : t('directionInbound'),
            },
          ]}
          label={t('direction')}
          value={direction}
          style={{ width: 490 }}
          onChange={(e) => {
            setDirection(e.target.value as 'outbound' | 'inbound' | undefined);
            resetAccounts();
          }}
          required
          // to have the same spacing as other inputs
          helperText=' '
        />
      </Grid>
      <Grid item>
        <AccountTableSingleSelect
          resource={debitResource}
          profileId={debitProfileId}
          label={debitLabel}
          filters={
            debitResource === 'tenant/accounts'
              ? ['number', 'currency']
              : ['number', 'alias', 'currency']
          }
          value={debitAccount?.alias || debitAccount?.number || debitAccountId}
          onClear={() => {
            setDebitAccount(undefined);
            setDebitAccountId(undefined);
          }}
          onChange={(account) => {
            setDebitAccount(account);
            setDebitAccountId(account.id);
            if (creditAccount && creditAccount.currency !== account.currency) {
              setCreditAccount(undefined);
              setCreditAccountId(undefined);
            }
          }}
          additionalFilters={{
            ...(debitResource === 'tenant/accounts'
              ? { type: direction === 'inbound' ? 'SETTLEMENT' : 'REGULAR' }
              : { type: 'REGULAR', status: ['ACTIVE', 'BLOCKED'] }),
            ...(creditAccount ? { currency: creditAccount.currency } : {}),
          }}
          disabled={!direction || (debitIsSettlement && !creditAccount)}
          required
          chipVariant='balance'
          style={{ width: 240 }}
        />
      </Grid>
      <Grid item>
        <AccountTableSingleSelect
          resource={creditResource}
          profileId={creditProfileId}
          label={creditLabel}
          filters={
            creditResource === 'tenant/accounts'
              ? ['number', 'currency']
              : ['number', 'alias', 'currency']
          }
          value={creditAccount?.alias || creditAccount?.number || creditAccountId}
          onClear={() => {
            setCreditAccount(undefined);
            setCreditAccountId(undefined);
          }}
          onChange={(account) => {
            setCreditAccount(account);
            setCreditAccountId(account.id);
            if (debitAccount && debitAccount.currency !== account.currency) {
              setDebitAccount(undefined);
              setDebitAccountId(undefined);
            }
          }}
          additionalFilters={{
            ...(creditResource === 'tenant/accounts'
              ? { type: direction === 'outbound' ? 'SETTLEMENT' : 'REGULAR' }
              : { type: 'REGULAR', status: ['ACTIVE', 'BLOCKED'] }),
            ...(debitAccount ? { currency: debitAccount.currency } : {}),
          }}
          disabled={!direction || (creditIsSettlement && !debitAccount)}
          required
          chipVariant='balance'
          style={{ width: 240 }}
        />
      </Grid>
      <Grid item>
        <AmountInput
          onChange={(newAmount) => {
            if (newAmount?.value !== amount?.value || newAmount?.currency !== amount?.currency) {
              setAmount(newAmount);
            }
          }}
          label={t('amount')}
          permanentSelectedCurrency={debitAccount?.currency || creditAccount?.currency}
          disabled={!debitAccount && !creditAccount}
          currencyDisabled
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('comment')}
          style={{ width: 490 }}
          multiline
          onChange={(e: any) => {
            debouncedSetComment(e.target.value);
          }}
          required
        />
      </Grid>
      <Grid item>
        <TextInput
          label={t('reference')}
          style={{ width: 282 }}
          onChange={(e: any) => {
            debouncedSetReference(e.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default SettlementForm;
